import { useAppContext } from "@/app/providers/AppContext";
import AddToWatchlistDialog from "@/domains/watches/components/AddToWatchlistDialog";
import FlowPivotTable from "@/domains/waves/components/FlowPivotTable";
import { FlowTableSkeleton } from "@/shared/components/PortalPageSkeletons";
import { formatLastUpdateDate } from "@/shared/lib/dateUtils";
import { queryKeys } from "@/shared/lib/queryKeys";
import type { FlowTrade } from "@/shared/lib/types";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, Pill } from "@/shared/ui/DesignLib";
import { Input } from "@/shared/ui/input";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/shared/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/ui/select";
import { Switch } from "@/shared/ui/switch";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/shared/ui/table";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/shared/ui/tooltip";
import { useQuery } from "@tanstack/react-query";
import {
    BarChart3,
    BellPlus,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Filter,
    Loader2,
    RotateCcw,
    Search,
    Trash2,
    X,
    Zap
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface OptionsFlowResponse {
  data?: FlowTrade[];
  date?: string | null;
  timestamp_yyyymmdd?: number | null;
}

const stockPriceOptions = [
  { label: '< $25', value: 'Under $25' },
  { label: '$25 - $75', value: '$25 - $75' },
  { label: '$75 - $150', value: '$75 - $150' },
  { label: '> $150', value: 'Over $150' }
];

const openInterestOptions = [
  { label: '< 1k', value: 'Under 1K OI' },
  { label: '1k to 5k', value: '1K - 5K OI' },
  { label: '5k to 25k', value: '5K - 25K OI' },
  { label: '> 25k', value: 'Over 25K OI' }
];

const volumeOptions = [
  { label: '< 1k', value: 'Under 1K Volume' },
  { label: '1k to 5k', value: '1K - 5K Volume' },
  { label: '5k to 25k', value: '5K - 25K Volume' },
  { label: '> 25k', value: 'Over 25K Volume' }
];

const getTickerDashboardSymbol = (ticker?: string) => {
  const normalized = (ticker || "").trim().toUpperCase();
  if (normalized === "SPX" || normalized === "SPXW") {
    return "SPY";
  }
  return normalized;
};

const parseFlowDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

interface FilterSectionProps {
  title: string;
  options: Array<{ label: string; value: string }>;
  activeFilters: string[];
  setActiveFilters: React.Dispatch<React.SetStateAction<string[]>>;
}

const FilterSection = ({ title, options, activeFilters, setActiveFilters }: FilterSectionProps) => (
  <div className="mb-6">
    <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => {
            const filterValue = option.value;
            setActiveFilters((prev: string[]) =>
              prev.includes(filterValue)
                ? prev.filter(f => f !== filterValue)
                : [...prev, filterValue]
            );
          }}
          className={`px-3 py-1.5 rounded-md text-xs transition-colors font-medium border ${activeFilters.includes(option.value)
            ? "bg-primary/20 text-primary border-primary/30"
            : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted/60"
            }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

const ZeroDTETab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { api, zdteOptions, setZdteOptions, zdteOptionsFetched, setZdteOptionsFetched } = useAppContext();
  const tickerPageSource = `${location.pathname}${location.search}`;
  const [loading, setLoading] = useState(!zdteOptionsFetched);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isPolling, setIsPolling] = useState(false);
  const [activeDate, setActiveDate] = useState<Date | null>(null);
  const [sortField, setSortField] = useState<string>("timestamp");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [showOFilters, setShowOFilters] = useState(false);
  const [showPivotView, setShowPivotView] = useState(false);
  const [selectedWatchTrade, setSelectedWatchTrade] = useState<FlowTrade | null>(null);
  const itemsPerPage = pageSize;

  const SortableHeader = ({ field, children, className = "" }: { field: string, children: React.ReactNode, className?: string }) => {
    const isActive = sortField === field;
    const isRightAligned = className.includes("text-right");

    return (
      <TableHead
        className={`cursor-pointer hover:text-foreground transition-colors select-none ${className}`}
        onClick={() => handleSort(field)}
      >
        <div className={`flex items-center gap-1 ${isRightAligned ? "justify-end" : ""}`}>
          {children}
          <div className="flex flex-col">
            <ChevronUp className={`w-3 h-3 ${isActive && sortDirection === 'asc' ? 'text-primary' : 'text-muted-foreground/30'}`} />
            <ChevronDown className={`w-3 h-3 -mt-1 ${isActive && sortDirection === 'desc' ? 'text-primary' : 'text-muted-foreground/30'}`} />
          </div>
        </div>
      </TableHead>
    );
  };

  useEffect(() => {
    // Reset page on search or filter change
    setCurrentPage(1);
  }, [searchTerm, activeFilters]);

  // Function to identify repeated trades (from design)
  const identifyRepeatedTrades = (trades: any[]) => {
    const WINDOW_MINUTES = 15;
    const tradeMap = new Map();

    trades.forEach(trade => {
      if (!trade) return;
      const ticker = trade.symbol || trade.ticker || 'unknown';
      const strike = trade.strike_price || 0;
      const cp = trade.c_p || 'unknown';
      const exp = trade.exp || 'unknown';
      const contractKey = `${ticker}-${strike}-${cp}-${exp}`;

      if (!tradeMap.has(contractKey)) {
        tradeMap.set(contractKey, []);
      }
      tradeMap.get(contractKey)?.push(trade);
    });

    const processedTrades: any[] = [];
    tradeMap.forEach((contractTrades, _) => {
      contractTrades.sort((a: any, b: any) => (a.timestamp || 0) - (b.timestamp || 0));

      let i = 0;
      while (i < contractTrades.length) {
        const windowStart = contractTrades[i].timestamp || 0;
        const windowEnd = windowStart + (WINDOW_MINUTES * 60 * 1000);

        const windowTrades = contractTrades.filter((trade: any) =>
          (trade.timestamp || 0) >= windowStart &&
          (trade.timestamp || 0) <= windowEnd
        );

        if (windowTrades.length > 1) {
          const buyTrades = windowTrades.filter((t: any) => t.bur_or_sell === "BOUGHT");
          const sellTrades = windowTrades.filter((t: any) => t.bur_or_sell === "SOLD");

          const totalVolume = windowTrades.reduce((sum: number, trade: any) => sum + (trade.size || 0), 0);

          const enhancedTrade = {
            ...windowTrades[0],
            repeat_count: windowTrades.length,
            buy_count: buyTrades.length,
            sell_count: sellTrades.length,
            total_volume: totalVolume,
            trades_sequence: windowTrades.map((t: any) => ({
              timestamp: t.timestamp,
              price: t.price,
              size: t.size,
              type: t.bur_or_sell
            }))
          };
          processedTrades.push(enhancedTrade);
        } else {
          processedTrades.push(contractTrades[i]);
        }
        i += Math.max(1, windowTrades.length);
      }
    });

    return processedTrades.sort((a: any, b: any) => (b.timestamp || 0) - (a.timestamp || 0));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await api<OptionsFlowResponse>(
        "POST",
        "/api/proxy/zdte",
        {
          query: {},
          page: 1,
          pageSize: 500,
          sort: [["timestamp", -1]],
        }
      );

      const responseDate = parseFlowDate(result?.date);
      if (result?.data?.length) {
        const processedData = identifyRepeatedTrades(result.data);
        setZdteOptions(processedData);
        setZdteOptionsFetched(true);
        setActiveDate(responseDate);
      } else {
        setZdteOptions([]);
        setZdteOptionsFetched(true);
        setActiveDate(responseDate);
        setError("No recent flow data found.");
      }
    } catch {
      setError("Failed to load options data. Please check your connection.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useQuery({
    queryKey: queryKeys.zeroDte(),
    queryFn: async () => {
      await fetchData();
      return true;
    },
    enabled: !zdteOptionsFetched,
    staleTime: 30_000,
  });

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isPolling) {
      interval = setInterval(() => {
        fetchData();
      }, 30000); // Poll every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPolling, fetchData]);

  const formatPremium = (prem: number) => {
    if (prem >= 1_000_000) return `$${(prem / 1_000_000).toFixed(2)}M`;
    if (prem >= 1_000) return `$${(prem / 1_000).toFixed(1)}K`;
    return `$${prem.toFixed(0)}`;
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return "-";

    // If it's a Unix timestamp (ms)
    if (typeof timestamp === 'number' && timestamp > 1000000000000) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString("en-US", { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }

    const str = timestamp.toString();
    if (str.length < 14) return str;

    // Format YYYYMMDDHHMMSS to HH:MM:SS
    const hour = str.slice(8, 10);
    const minute = str.slice(10, 12);
    const second = str.slice(12, 14);
    return `${hour}:${minute}:${second}`;
  };

  const formatExpiration = (exp: any) => {
    if (!exp) return "-";

    const str = exp.toString().padStart(6, "0");
    if (str.length !== 6) return exp;

    // Parse YYMMDD format
    const yy = parseInt(str.slice(0, 2));
    const mm = str.slice(2, 4);
    const dd = str.slice(4, 6);

    // Convert YY to full year (for options, usually recent dates)
    const fullYear = yy < 50 ? 2000 + yy : 1900 + yy;

    return `${mm}/${dd}/${fullYear}`;
  };

  const filteredData = useMemo(() => {
    let data = zdteOptions || [];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      data = data.filter((item: any) =>
        item.ticker?.toLowerCase().includes(searchLower) ||
        item.symbol_full?.toLowerCase().includes(searchLower)
      );
    }

    // Advanced filters
    if (activeFilters.length > 0) {
      const filterGroups: { [key: string]: string[] } = {
        direction: ["Calls", "Puts"],
        side: ["Bought", "Sold"],
        dte: ["1", "3", "7", "15", "30", "60", "90"],
        stockPrice: ["Under $25", "$25 - $75", "$75 - $150", "Over $150"],
        oi: ["Under 1K OI", "1K - 5K OI", "5K - 25K OI", "Over 25K OI"],
        vol: ["Under 1K Volume", "1K - 5K Volume", "5K - 25K Volume", "Over 25K Volume"],
        spread: ["Spreads", "Non-Spreads"],
        bidAsk: ["Above Ask", "Below Bid", "At Ask", "At Bid"]
      };

      // Group active filters by their categories
      const activeGroups: { [key: string]: string[] } = {};
      activeFilters.forEach(f => {
        let groupName = "standalone";
        for (const [name, filters] of Object.entries(filterGroups)) {
          if (filters.includes(f)) {
            groupName = name;
            break;
          }
        }

        const key = groupName === "standalone" ? f : groupName;
        if (!activeGroups[key]) activeGroups[key] = [];
        activeGroups[key].push(f);
      });

      data = data.filter((trade: any) => {
        // Must match at least one filter in EVERY active group (AND between groups, OR within groups)
        return Object.values(activeGroups).every(groupFilters => {
          return groupFilters.some(f => {
            // Direction
            if (f === "Calls") return trade.c_p === "C";
            if (f === "Puts") return trade.c_p === "P";
            if (f === "Bought") return trade.bur_or_sell === "BOUGHT";
            if (f === "Sold") return trade.bur_or_sell === "SOLD";

            // Trade Types
            if (f === "Sweeps") return trade.conditions?.includes(501) || trade.conditions?.includes(204);
            if (f === "Blocks") return trade.conditions?.includes(502);
            if (f === "Splits") return trade.conditions?.includes(503);
            if (f === "Complex") return trade.conditions?.includes(504);

            // Price / Volume
            if (f === ">$1 Million") return trade.prem_price >= 1_000_000;
            if (f === "Volume > OI") return (trade.snapshot_day_volume || 0) > (trade.snapshot_open_interest || 0);

            // Relative to Bid/Ask
            if (f === "Above Ask") return trade.price > (trade.snapshot_last_quote_ask || 0);
            if (f === "Below Bid") return trade.price < (trade.snapshot_last_quote_bid || 0);

            const isEqual = (a: number, b: number, tol = 0.01) => Math.abs(a - b) < tol;
            if (f === "At Ask") return isEqual(trade.price, trade.snapshot_last_quote_ask || 0);
            if (f === "At Bid") return isEqual(trade.price, trade.snapshot_last_quote_bid || 0);

            // Expiry
            if (f === "Short Expiry") return trade.tte <= 7;
            if (f === "LEAPS") return trade.tte >= 365;

            // Days Till Expiration (DTE) options
            if (["1", "3", "7", "15", "30", "60", "90"].includes(f)) {
              return (trade.tte || 0) <= parseInt(f);
            }

            // Stock Price filters
            if (f === "Under $25") return trade.price < 25;
            if (f === "$25 - $75") return trade.price >= 25 && trade.price <= 75;
            if (f === "$75 - $150") return trade.price >= 75 && trade.price <= 150;
            if (f === "Over $150") return trade.price > 150;

            // Open Interest filters
            if (f === "Under 1K OI") return (trade.snapshot_open_interest || 0) < 1000;
            if (f === "1K - 5K OI") return (trade.snapshot_open_interest || 0) >= 1000 && (trade.snapshot_open_interest || 0) <= 5000;
            if (f === "5K - 25K OI") return (trade.snapshot_open_interest || 0) >= 5000 && (trade.snapshot_open_interest || 0) <= 25000;
            if (f === "Over 25K OI") return (trade.snapshot_open_interest || 0) > 25000;

            // Volume filters
            if (f === "Under 1K Volume") return (trade.snapshot_day_volume || 0) < 1000;
            if (f === "1K - 5K Volume") return (trade.snapshot_day_volume || 0) >= 1000 && (trade.snapshot_day_volume || 0) <= 5000;
            if (f === "5K - 25K Volume") return (trade.snapshot_day_volume || 0) >= 5000 && (trade.snapshot_day_volume || 0) <= 25000;
            if (f === "Over 25K Volume") return (trade.snapshot_day_volume || 0) > 25000;

            // Custom list filters
            if (f === "Non-ETF") return !["SPY", "QQQ", "IWM"].includes(trade.ticker);
            if (f === "Non-Spreads") return !trade.conditions?.includes(202);
            if (f === "Spreads") return trade.conditions?.includes(202);
            if (f === "Opening Order") return trade.conditions?.includes(203);
            if (f === "Repeat Flow") return trade.conditions?.includes(402) || (trade.repeat_count && trade.repeat_count > 1);

            return true;
          });
        });
      });
    }

    return data;
  }, [zdteOptions, searchTerm, activeFilters]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortField, sortDirection]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 font-sans">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1
            className="watches-page-title font-bold flex items-center gap-2 text-foreground"
            style={{ margin: 0, lineHeight: 1.1 }}
          >
            <Zap className="text-primary w-5 h-5" />
            0 DTE Options Flow
            {activeDate && (
              <Badge variant="secondary" className="ml-2 font-sans text-[10px] uppercase font-bold tracking-wider">
                {activeDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground text-sm">
            Real-time zero days to expiration trade data.
            {activeDate && (
              <span className="ml-2 text-muted-foreground/70">
                Last update - {formatLastUpdateDate(activeDate)}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant={showPivotView ? "default" : "outline"}
            size="sm"
            onClick={() => setShowPivotView((value) => !value)}
            aria-pressed={showPivotView}
            className="h-9 rounded-full px-3 text-xs font-bold"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Pivot View
          </Button>
          <div className="flex items-center gap-2 bg-muted/30 px-3 py-1.5 rounded-full border border-border/50">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-tight">Live Refresh</span>
            <Switch checked={isPolling} onCheckedChange={setIsPolling} className="scale-75" />
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="h-9 w-9 p-0 rounded-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search tickers (e.g. SPY, AAPL)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-muted/20 border-border/40 focus:border-primary/40 h-10"
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <Popover open={showOFilters} onOpenChange={setShowOFilters}>
            <PopoverTrigger asChild disabled={loading}>
              <Button variant="outline" className="flex items-center gap-2 h-10 bg-muted/20 border-border/40">
                <Filter className="w-4 h-4" />
                <span className="text-sm">Filters</span>
                {activeFilters.length > 0 && (
                  <Badge variant="default" className="h-5 min-w-[20px] px-1 rounded-full flex items-center justify-center text-[10px]">
                    {activeFilters.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-80 p-4 max-h-[450px] overflow-auto bg-card border-border/60 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-sm font-bold uppercase tracking-wider">Market Filters</h2>
                <button
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowOFilters(false)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-3 tracking-widest">General</h3>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Calls", "Puts", "Bought", "Sold", "Sweeps",
                      "Above Ask", "Below Bid", "Volume > OI", "Short Expiry", "LEAPS",
                      ">$1 Million", "Non-ETF", "Non-Spreads", "Spreads", "Opening Order",
                      "Repeat Flow"
                    ].map((filter) => (
                      <button
                        key={filter}
                        onClick={() => {
                          setActiveFilters((prev: string[]) =>
                            prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]
                          );
                        }}
                        className={`px-2.5 py-1.5 rounded text-[11px] font-medium border transition-all ${activeFilters.includes(filter)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted/60"
                          }`}
                      >
                        {filter}
                      </button>
                    ))}
                  </div>
                </div>

                <FilterSection
                  title="Stock Price"
                  options={stockPriceOptions}
                  activeFilters={activeFilters}
                  setActiveFilters={setActiveFilters}
                />

                <FilterSection
                  title="Open Interest"
                  options={openInterestOptions}
                  activeFilters={activeFilters}
                  setActiveFilters={setActiveFilters}
                />

                <FilterSection
                  title="Volume"
                  options={volumeOptions}
                  activeFilters={activeFilters}
                  setActiveFilters={setActiveFilters}
                />
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            className="h-10 px-3 bg-muted/20 border-border/40 text-muted-foreground hover:text-foreground"
            onClick={() => {
              setActiveFilters([]);
              setSearchTerm("");
            }}
            disabled={activeFilters.length === 0 && !searchTerm}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            <span className="text-sm">Reset</span>
          </Button>
        </div>
      </div>

      {showPivotView && <FlowPivotTable trades={sortedData} title="0 DTE Flow Pivot" />}

      <Card>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {loading && zdteOptions.length === 0 ? (
            <FlowTableSkeleton />
          ) : sortedData.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center" }}>
              <span className="font-sans text-[12px] text-muted-foreground">No trades found matching your active filters.</span>
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 border-border/50 hover:bg-muted/30">
                    <SortableHeader field="timestamp" className="pl-6 w-[100px]">TIME</SortableHeader>
                    <SortableHeader field="ticker" className="w-[120px]">SYMBOL</SortableHeader>
                    <TableHead className="w-[80px] text-[10px] font-bold uppercase tracking-wider">TYPE</TableHead>
                    <SortableHeader field="strike_price" className="text-right w-[100px]">STRIKE</SortableHeader>
                    <SortableHeader field="size" className="text-right w-[110px]">SIZE</SortableHeader>
                    <SortableHeader field="price" className="text-right w-[100px]">PRICE</SortableHeader>
                    <SortableHeader field="prem_price" className="text-right w-[120px]">PREMIUM</SortableHeader>
                    <SortableHeader field="exp" className="text-right w-[100px]">EXP</SortableHeader>
                    <TableHead className="text-right pr-6 w-[80px] text-[10px] font-bold uppercase tracking-wider">
                      ACTIONS
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedData.map((trade: any, idx: number) => (
                    <TableRow key={`${trade.id || idx}`} className="border-border/40 hover:bg-muted/20 transition-colors group">
                      <TableCell className="pl-6 font-sans text-[11px] text-muted-foreground group-hover:text-foreground">
                        {formatTimestamp(trade.timestamp)}
                      </TableCell>
                      <TableCell className="font-bold text-[13px]">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/ticker/${getTickerDashboardSymbol(trade.ticker)}`, {
                                state: { from: tickerPageSource },
                              })
                            }
                            className="text-primary tracking-tight font-sans hover:underline"
                          >
                            {trade.ticker}
                          </button>
                          <div className="flex gap-1">
                            {trade.conditions?.includes(204) && <Tooltip><TooltipTrigger><Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[9px] px-1.5 h-4 uppercase font-black">S</Badge></TooltipTrigger><TooltipContent>Sweep</TooltipContent></Tooltip>}
                            {trade.repeat_count && trade.repeat_count > 1 && <Tooltip><TooltipTrigger><Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-[9px] px-1.5 h-4 uppercase font-black">R</Badge></TooltipTrigger><TooltipContent>Repeat Flow ({trade.repeat_count}x)</TooltipContent></Tooltip>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Pill active={trade.c_p === "C"} color={trade.c_p === "C" ? "#4ade80" : "#f87171"}>
                          {trade.c_p === "C" ? "CALL" : "PUT"}
                        </Pill>
                      </TableCell>
                      <TableCell className="text-right font-sans text-[12px] font-semibold">
                        ${trade.strike_price}
                      </TableCell>
                      <TableCell className="text-right font-sans text-[12px] text-muted-foreground group-hover:text-foreground">
                        {trade.size?.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-sans text-[12px]">
                        ${trade.price?.toFixed(2)}
                      </TableCell>
                      <TableCell className={`text-right font-sans text-[12px] font-bold ${trade.prem_price >= 1000000 ? "text-primary italic underline underline-offset-4" : "text-foreground"}`}>
                        {formatPremium(trade.prem_price)}
                      </TableCell>
                      <TableCell className="text-right font-sans text-[11px] text-muted-foreground">
                        {formatExpiration(trade.exp)}
                      </TableCell>
                      <TableCell className="text-right pr-6">
                        <button
                          type="button"
                          onClick={() => setSelectedWatchTrade(trade)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border/60 text-muted-foreground transition-colors hover:border-primary/60 hover:bg-primary/10 hover:text-primary"
                          aria-label={`Add ${trade.ticker} to watchlist`}
                        >
                          <BellPlus className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* New Modern Pagination */}
          {!loading && sortedData.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-border/50 bg-muted/10">
              <div className="text-[11px] text-muted-foreground font-sans">
                Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedData.length)} of {sortedData.length} entries
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase">Page size</span>
                  <Select value={pageSize.toString()} onValueChange={(val) => { setPageSize(parseInt(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 w-[70px] text-[11px] bg-background border-border/40 font-sans">
                      <SelectValue placeholder={pageSize.toString()} />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border/60">
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full border-border/40"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  <div className="flex items-center px-3 py-1 bg-background border border-border/40 rounded-full">
                    <span className="text-[11px] font-bold">{currentPage}</span>
                    <span className="mx-1.5 text-[10px] text-muted-foreground">/</span>
                    <span className="text-[11px] text-muted-foreground">{totalPages}</span>
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full border-border/40"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm flex items-center gap-3">
          <div className="w-1.5 h-1.5 bg-destructive rounded-full animate-pulse" />
          {error}
        </div>
      )}

      <AddToWatchlistDialog
        key={
          selectedWatchTrade?.contract ??
          selectedWatchTrade?.symbol ??
          "closed"
        }
        open={Boolean(selectedWatchTrade)}
        trade={selectedWatchTrade}
        onOpenChange={(open) => {
          if (!open) setSelectedWatchTrade(null);
        }}
      />
    </div>
  );
};

export default ZeroDTETab;
